import { useEffect, type ReactNode } from "react";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import { useT } from "@transifex/react";
import LoadingSpinner from "../../basic/loadingSpinner/LoadingSpinner";
import LegalFooter from "../../complex/legalFooter/LegalFooter";
import { useClassMember } from "../../context/ClassMemberContext";
import { useStudentReviewContext } from "../../context/StudentReviewTestContext";
import { resolveSharedResultAccess, SharedResultAccessStates } from "../../helpers/SharedResultAccess";
import { getClassroomLoginRouteWithReturnTo, getClassroomRootRoute } from "../../helpers/schoolClassHelpers";
import ContentWithHeader from "../../wrapper/ContentWithHeader/ContentWithHeader";
import WrongClassMemberNotice from "./components/WrongClassMemberNotice";

interface StudentResultAccessGuardProps {
    children: ReactNode;
}

/**
 * Blocks a member-owned shared result (/result/:shareId) from rendering for anyone but the owning
 * class member. Legacy anonymous share links (results without a classMemberId) pass through freely.
 */
const StudentResultAccessGuard = (props: StudentResultAccessGuardProps) => {
    const t = useT();
    const { shareId } = useParams<{ shareId: string }>();
    const location = useLocation();
    const history = useHistory();
    const { setShareId, dataLoaded, resultLoadStatus, resultData, studentTest } = useStudentReviewContext();
    const { member, isRestoringSession, logout } = useClassMember();

    useEffect(() => {
        if (shareId) {
            setShareId(shareId);
        }
    }, [shareId, setShareId]);

    const t_sr_loading = t("Loading", {
        _tags: "StudentResult, StudentResultAccessGuard, ScreenReaderOnly",
    });

    const access = resolveSharedResultAccess({
        dataLoaded,
        isRestoringSession,
        resultLoadStatus,
        resultClassMemberId: resultData.classMemberId,
        memberId: member?.id,
    });

    if (access === SharedResultAccessStates.ALLOW) {
        return <>{props.children}</>;
    }

    // Always targets the class the result belongs to — a member seeing this page is typically logged
    // into a different teacher's classroom for the same school class, so their own class login would
    // be a dead end. groupId is settled once resultLoadStatus is "LOADED"; missing means the test
    // carries no class (or the teacher-test fetch failed), so fall back to the classroom root, where
    // any class code can be entered. Either way returnTo brings them back to this result.
    const getResultClassLoginPath = (): string => {
        const returnTo = `${location.pathname}${location.search}`;
        if (studentTest.groupId) {
            return getClassroomLoginRouteWithReturnTo(studentTest.groupId, returnTo);
        }
        return `${getClassroomRootRoute()}?returnTo=${encodeURIComponent(returnTo)}`;
    };

    const handleSwitchAccount = () => {
        const target = getResultClassLoginPath();
        logout();
        history.replace(target);
    };

    if (access === SharedResultAccessStates.LOGIN_REQUIRED) {
        return <Redirect to={getResultClassLoginPath()} />;
    }

    const renderBody = () => {
        if (access === SharedResultAccessStates.WRONG_MEMBER && member) {
            // No automatic redirect to the classroom login here: it would bounce a logged-in member
            // of the same class straight back via returnTo, looping forever. The switch-account
            // action below is user-initiated, so it can safely carry a returnTo.
            return (
                <WrongClassMemberNotice
                    memberName={member.name}
                    schoolClassId={member.schoolClassId}
                    onSwitchAccount={handleSwitchAccount}
                />
            );
        }
        return (
            <div
                role="status"
                aria-live="polite"
            >
                <LoadingSpinner />
                <span className="sr-only">{t_sr_loading}</span>
            </div>
        );
    };

    return (
        // Gray page background so the white notice card reads as a card, matching the online-test
        // Sidney cards it shares a look with.
        <ContentWithHeader
            hasGrayBackground
            footer={<LegalFooter />}
        >
            <div className="flex flex-1 flex-col w-full">
                <div className="flex flex-1 items-center justify-center p-s w-full max-w-[1000px] mx-auto">
                    {renderBody()}
                </div>
            </div>
        </ContentWithHeader>
    );
};

export default StudentResultAccessGuard;
